import React, { useState } from "react";
import { Phone, MousePointerClick, UserCheck } from "lucide-react";
import { AudioPlayer } from "@/components/AudioPlayer";
import { CallAiSection } from "@/components/CallAiSection";
import { LineBadge } from "@/components/LineBadge";
import { StatusBadge, DirectionBadge } from "@/components/CallList";
import { recordingUrl } from "@/lib/api";
import { formatCallTime, formatCallDuration, initials } from "@/lib/format";

const EmptyState = () => (
  <div data-testid="detail-empty" className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center flex flex-col items-center">
    <span className="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <MousePointerClick className="h-6 w-6 text-slate-400" />
    </span>
    <p className="font-heading font-semibold text-slate-800">Aucun appel sélectionné</p>
    <p className="text-sm text-slate-400 mt-1 max-w-[220px]">Sélectionnez un appel pour afficher ses détails.</p>
  </div>
);

export const CallDetailPanel = ({ call }) => {
  const [seekSignal, setSeekSignal] = useState(null);
  if (!call) return <EmptyState />;

  return (
    <div data-testid="call-detail-panel" className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <div className="flex items-center gap-3">
          <span className="h-12 w-12 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-sm font-semibold text-slate-500">
            {initials(call.name)}
          </span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-slate-900 truncate">{call.name}</p>
            <p className="text-sm text-slate-400 tabular-nums">{call.number}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <DirectionBadge direction={call.direction} />
          <StatusBadge status={call.status} />
          <LineBadge line={call.line} testid="line-badge-detail" />
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
            <Phone className="h-3 w-3" />
            {formatCallTime(call.started_at)}
          </span>
          {formatCallDuration(call) !== "—" && (
            <span data-testid="duration-pill" className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 tabular-nums">
              {formatCallDuration(call)}
            </span>
          )}
        </div>
        {call.answered_by && (
          <p className="mt-3 text-sm text-slate-600 inline-flex items-center gap-1.5" data-testid="answered-by">
            <UserCheck className="h-4 w-4 text-emerald-600" /> Répondu par <span className="font-medium">{call.answered_by}</span>
          </p>
        )}
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
        <p className="text-xs tracking-[0.05em] uppercase font-semibold text-slate-500 mb-3">Enregistrement</p>
        {call.has_recording ? (
          <AudioPlayer src={recordingUrl(call.id)} duration={call.recording_duration_seconds} seekSignal={seekSignal} />
        ) : (
          <>
            <AudioPlayer disabled />
            <p className="text-xs text-slate-400 mt-2">Aucun enregistrement disponible pour cet appel.</p>
          </>
        )}
      </div>

      <CallAiSection
        callId={call.id}
        hasRecording={call.has_recording}
        onSeek={(t) => setSeekSignal({ t, n: Date.now() })}
      />
    </div>
  );
};
